/* @sravni/react-design-system version 0.2.65 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import addMonth from 'date-fns/addMonths';
import startOfDay from 'date-fns/startOfDay';
import { useBoolean, useStyles, safeInvoke } from '@sravni/react-utils';
import { isValid } from '../utils/date.js';
import Header from './Header.js';
import Month from './Month.js';
import modules_75b426be from '../styles/Calendar.module.less.js';

/**
 * @description Calendar component.
 *
 * @component
 * @example
 * ```jsx
 * <Calendar />
 * ```
 */
var Calendar = React.forwardRef(function (props, ref) {
    var minDate = props.minDate, maxDate = props.maxDate, rangeLimit = props.rangeLimit, disabledIntervals = props.disabledIntervals, _a = props.mode, mode = _a === void 0 ? 'single' : _a, selection = props.selection, _b = props.defaultDate, defaultDate = _b === void 0 ? minDate !== undefined ? minDate : new Date() : _b, className = props.className, onChange = props.onChange, restProps = __rest(props, ["minDate", "maxDate", "rangeLimit", "disabledIntervals", "mode", "selection", "defaultDate", "className", "onChange"]);
    var _c = __read(React.useState(function () {
        if (selection && selection.from && isValid(selection.from)) {
            return selection.from;
        }
        return defaultDate;
    }), 2), activeMonth = _c[0], setActiveMonth = _c[1];
    var _d = __read(React.useState(selection && startOfDay(selection.from)), 2), selectionStart = _d[0], setSelectionStart = _d[1];
    var _e = __read(React.useState(selection && startOfDay(selection.to)), 2), selectionEnd = _e[0], setSelectionEnd = _e[1];
    var _f = __read(useBoolean(false), 2), selectionInProgress = _f[0], setSelectionInProgress = _f[1];
    var cx = useStyles(modules_75b426be);
    var classNames = cx(className, 'container');
    var normalizedMinDate = minDate ? startOfDay(minDate) : minDate;
    var handleChangeMonth = React.useCallback(function (direction) {
        switch (direction) {
            case 'prev':
                return setActiveMonth(addMonth(activeMonth, -1));
            case 'next':
                return setActiveMonth(addMonth(activeMonth, 1));
        }
    }, [activeMonth]);
    var handleChangeSelected = React.useCallback(function (range) {
        setSelectionStart(range.from);
        setSelectionEnd(range.to);
        if (mode === 'range') {
            selectionInProgress && safeInvoke(onChange, range);
            setSelectionInProgress.toggle();
        }
        else {
            safeInvoke(onChange, range);
            setSelectionInProgress.off();
        }
    }, [selectionInProgress, mode, setSelectionInProgress, onChange]);
    return (React.createElement("div", __assign({ ref: ref, "data-qa": "Calendar", className: classNames }, restProps),
        React.createElement(Header, { month: activeMonth, onChange: handleChangeMonth }),
        React.createElement(Month, { month: activeMonth, minDate: normalizedMinDate, maxDate: maxDate, rangeLimit: rangeLimit, disabledIntervals: disabledIntervals, mode: mode, selectionStart: selectionStart, selectionEnd: selectionEnd, selectionInProgress: selectionInProgress, onChange: handleChangeSelected })));
});
Calendar.defaultProps = {
    mode: 'single',
};
Calendar.displayName = 'Calendar';

export default Calendar;
//# sourceMappingURL=Calendar.js.map
